// Third party
import moment from "moment";

// Interfaces
import { ICommissionCycle, IPayPeriod } from "@/Common/api/types/CommissionCycle";
import { IBuildPayPeriodsTreeArgs, IPeriodTreeLeaf } from "./interfaces";

// Helpers
import {
	getCurrentCommissionCycle,
	getCurrentPayPeriod,
	getPayPeriodTitleExtended,
	sortCollectionBy,
	getLatestPayPeriodInThisCC,
} from "@Common/helpers/actions";
import { ISideTreeBadge } from "../SideTreeView/interfaces";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const isSinglePeriod = (payPeriods: IPayPeriod[] = []) => Boolean(payPeriods?.length === 1);

const getFormattedDate = (date: Date | string | undefined, formatted = DATE_FORMAT.yearOnly) => {
	if (!date) {
		return "";
	}
	return moment(date).utc().format(formatted);
};

const getLastPeriodId = (args: {
	commissionCycle: Partial<ICommissionCycle>;
	payPeriods: IPayPeriod[];
	disablePayPeriodNotLast: boolean;
}) => {
	const { commissionCycle, payPeriods = [], disablePayPeriodNotLast = false } = args ?? {};
	const cycleEndDate = commissionCycle.endDate ? new Date(commissionCycle.endDate) : "";

	const isNotSortable = !cycleEndDate || !disablePayPeriodNotLast;
	if (!isNotSortable) {
		return payPeriods[payPeriods?.length - 1]._id;
	}

	const lastPayPeriodBeforeOrOnEndDate = payPeriods.filter((payPeriod) => new Date(payPeriod?.endDate) <= cycleEndDate).pop();
	return lastPayPeriodBeforeOrOnEndDate ? lastPayPeriodBeforeOrOnEndDate._id : "";
};

const getPayPeriodId = (args: {
	commissionCycle: Partial<ICommissionCycle>;
	payPeriod: Partial<IPayPeriod>;
	onlyPayPeriodId?: boolean;
}) => {
	const { commissionCycle, payPeriod, onlyPayPeriodId = false } = args ?? {};
	if (onlyPayPeriodId) {
		return payPeriod._id;
	}
	return `${commissionCycle._id}_${payPeriod._id}`;
};

const getMappedPeriods = (args: {
	year: string;
	commissionCycle: Partial<ICommissionCycle>;
	payPeriods: IPayPeriod[];
	lastPayPeriodId: string;
	disablePayPeriodNotLast: boolean;
	onlyPayPeriodId?: boolean;
	customBadges: Record<string, ISideTreeBadge>;
}) => {
	const {
		year,
		commissionCycle,
		payPeriods = [],
		lastPayPeriodId = "",
		disablePayPeriodNotLast = false,
		onlyPayPeriodId = false,
		customBadges = {},
	} = args ?? {};

	if (!payPeriods?.length) {
		return [];
	}

	const singlePayPeriod = isSinglePeriod(payPeriods);

	return payPeriods.map((payPeriod) => {
		const payPeriodRelativeId = getPayPeriodId({ commissionCycle, payPeriod, onlyPayPeriodId });
		const badge = customBadges[payPeriodRelativeId ?? ""];
		return {
			year,
			singlePayPeriod,
			badge,
			_id: payPeriodRelativeId,
			payPeriodId: payPeriod._id,
			disabled: disablePayPeriodNotLast && payPeriod._id !== lastPayPeriodId,
			title: payPeriod.title,
			titleExtended: getPayPeriodTitleExtended(commissionCycle, payPeriod.title),
			startDate: payPeriod.startDate,
			endDate: payPeriod.endDate,
			cycleTitle: commissionCycle.title,
			cycleId: commissionCycle._id,
			cycleStatus: commissionCycle.status,
			inReview: payPeriod.inReview ?? false,
			positionStatus: payPeriod.positionStatus,
			payPeriodProviders: payPeriod.payPeriodProviders,
			payPeriodPreviousCCId: payPeriod.previousCCId,
			payPeriodNextCCId: payPeriod.nextCCId,
			previousCCId: payPeriod.previousCCId,
			nextCCId: payPeriod.nextCCId,
			status: payPeriod.status,
		};
	});
};

const getMappedDefaultPeriod = (args: {
	year: string;
	commissionCycle: Partial<ICommissionCycle>;
	payPeriod?: IPayPeriod;
	onlyPayPeriodId?: boolean;
}) => {
	const { year, commissionCycle, payPeriod, onlyPayPeriodId = false } = args ?? {};
	if (!year || !commissionCycle || !payPeriod) {
		return null;
	}

	const payPeriodRelativeId = getPayPeriodId({ commissionCycle, payPeriod, onlyPayPeriodId });
	return {
		...payPeriod,
		year,
		_id: payPeriodRelativeId,
		payPeriodId: payPeriod._id,
		titleExtended: getPayPeriodTitleExtended(commissionCycle, payPeriod.title),
		cycleTitle: commissionCycle.title,
		cycleId: commissionCycle._id,
		cycleStatus: commissionCycle.status,
	};
};

const getDefaultPayPeriod = (args: { year: string; commissionCycle: Partial<ICommissionCycle>; disablePayPeriodNotLast: boolean }) => {
	const { year, commissionCycle, disablePayPeriodNotLast = false } = args ?? {};
	if (disablePayPeriodNotLast) {
		return getMappedDefaultPeriod({
			year,
			commissionCycle,
			payPeriod: getLatestPayPeriodInThisCC(commissionCycle),
		});
	}
	return getMappedDefaultPeriod({
		year,
		commissionCycle,
		payPeriod: getCurrentPayPeriod(commissionCycle.payPeriods),
	});
};

const getExpandedPeriod = (args: { payPeriod: Partial<IPeriodTreeLeaf> | null }): string[] => {
	const { payPeriod } = args ?? {};
	const { year = "", cycleId = "", _id = "" } = payPeriod ?? {};
	return [year, cycleId, _id];
};

const getMappedTree = (args: {
	list: ICommissionCycle[];
	customBadges: Record<string, ISideTreeBadge>;
}): Record<string, Partial<IPeriodTreeLeaf>> => {
	const { list = [], customBadges = {} } = args ?? {};
	if (!list?.length) {
		return {};
	}

	return list.reduce((items, cycle) => {
		const year = getFormattedDate(cycle?.startDate, DATE_FORMAT.yearOnly).toString();
		const badge = customBadges[year];
		items[year] = {
			_id: year,
			title: String(year),
			bold: true,
			list: [],
			badge,
		};
		return items;
	}, {} as Record<string, Partial<IPeriodTreeLeaf>>);
};

const getAttributeOrEmpty = (object: object, key = "_id") => {
	if (!object || !key) {
		return "";
	}
	return object[key as keyof object] ?? "";
};

const getCommissionCycleLeaf = (args: {
	year: string;
	commissionCycle: ICommissionCycle;
	periods: IPeriodTreeLeaf[];
	customBadges: Record<string, ISideTreeBadge>;
}): IPeriodTreeLeaf => {
	const { year, commissionCycle, periods = [], customBadges } = args ?? {};
	const sortedPeriods = sortCollectionBy(periods, "startDate");
	const singlePayPeriod = isSinglePeriod(commissionCycle.payPeriods);
	const badge = customBadges[getAttributeOrEmpty(commissionCycle)];
	return {
		year,
		singlePayPeriod,
		badge,
		_id: getAttributeOrEmpty(commissionCycle),
		cycleId: getAttributeOrEmpty(commissionCycle),
		title: getAttributeOrEmpty(commissionCycle, "title"),
		titleExtended: getAttributeOrEmpty(commissionCycle, "title"),
		cycleTitle: getAttributeOrEmpty(commissionCycle, "title"),
		status: getAttributeOrEmpty(commissionCycle, "status"),
		cycleStatus: getAttributeOrEmpty(commissionCycle, "status"),
		startDate: commissionCycle?.startDate ?? periods[0]?.startDate ?? "",
		endDate: commissionCycle?.endDate ?? periods[0]?.endDate ?? "",
		dataUpdatedAt: commissionCycle?.dataUpdatedAt,
		payPeriods: [...sortedPeriods],
		list: [...sortedPeriods],
		// Additional required fields (but not used in this tree level)
		positionStatus: [],
		payPeriodProviders: [],
		payPeriodPreviousCCId: "",
		payPeriodNextCCId: "",
		inReview: false,
		provider: "",
		description: "",
		payPeriodId: "",
		submitByDate: commissionCycle?.dataUpdatedAt,
		checkDate: commissionCycle?.dataUpdatedAt,
		drawIngested: false,
		lastModifiedOn: commissionCycle?.dataUpdatedAt,
	};
};

const getPayPeriodTree = (args: IBuildPayPeriodsTreeArgs) => {
	const {
		list = [],
		disablePayPeriodNotLast = false,
		isMultiple = false,
		onlyActive = false,
		onlyPayPeriodId = false,
		customBadges = {},
		defaultByDate = false,
	} = args;

	if (!list?.length) {
		return {
			isMultiple,
			tree: [],
			expanded: [],
			defaultPayPeriod: null,
		};
	}

	const treeMap = getMappedTree({ list, customBadges });

	for (const element of list) {
		const { payPeriods = [] } = element;
		const year = getFormattedDate(element?.startDate, DATE_FORMAT.yearOnly);
		const lastPayPeriodId = getLastPeriodId({ commissionCycle: element, payPeriods, disablePayPeriodNotLast });
		const cyclePeriods: Partial<IPeriodTreeLeaf>[] = getMappedPeriods({
			year,
			payPeriods,
			lastPayPeriodId,
			disablePayPeriodNotLast,
			onlyPayPeriodId,
			customBadges,
			commissionCycle: element,
		});

		const cycleLeaf = getCommissionCycleLeaf({
			year,
			customBadges,
			commissionCycle: element,
			periods: cyclePeriods as IPeriodTreeLeaf[],
		});
		const previous = treeMap?.[year]?.["list"] ?? [];
		treeMap[year]["list"] = [...previous, cycleLeaf];
	}

	const tree = Object.values(treeMap);
	const defaultCommissionCycle = getCurrentCommissionCycle(list, onlyActive, defaultByDate);

	if (!tree?.length || !defaultCommissionCycle) {
		return {
			tree,
			isMultiple,
			expanded: [],
			defaultPayPeriod: null,
		};
	}

	const defaultYear = getFormattedDate(defaultCommissionCycle?.startDate, DATE_FORMAT.yearOnly);
	const defaultPayPeriod = getDefaultPayPeriod({
		year: defaultYear,
		commissionCycle: defaultCommissionCycle,
		disablePayPeriodNotLast,
	});
	const expanded = getExpandedPeriod({ payPeriod: defaultPayPeriod });

	return {
		tree,
		isMultiple,
		defaultPayPeriod,
		expanded,
	};
};

export { getPayPeriodTree };
